
import { expect, is, test } from "@benchristel/taste"
import Avatar from "@mui/material/Avatar"
import Paper from "@mui/material/Paper"
import Stack from "@mui/material/Stack"
import { useState } from "preact/hooks"
import useTimeout from "react-hook-timeout"
import { EditableList, Item } from "./EditableList"
import { PageTitle } from "./lib/PageTitle"
import { PancakeStack } from "./PancakeStack"
import type { PollPhenomenon } from "./poll"
import { ballotFor } from "./poll"
import { PollNavigation } from "./PollNavigation"
import { SyncBar } from "./SyncBar"
import ProfileMenu from "./ProfileMenu"
import { TitleField } from "./TitleField"

type BallotViewProps = {
  poll: PollPhenomenon
  setTitle: (title: string) => unknown
  onAddCandidate: (c: Item) => unknown
  onRemoveItem: any
  onBallotUpdated: (r: string[]) => void
  activeProfile: string,
  setProfileID: (id: string) => void 
}

export function BallotView({
  setTitle,
  onAddCandidate,
  onRemoveItem,
  onBallotUpdated,
  poll,
  activeProfile,
  setProfileID
}: BallotViewProps) {
  const { title: pollTitle, id: pollID, ballots } = poll
  const [showSyncing, setShowSyncing] = useState<boolean>(false)
  const { setTimeout } = useTimeout()

  return (
    <>
      <PageTitle value={pageTitle(pollTitle)} />
      <PancakeStack>
        <Paper>
          <Stack p={2} direction="row" spacing={1} alignItems="center">
            <TitleField
              value={pollTitle}
              label="Poll Title"
              placeholder={"e.g. Who is the greatest rock band of all time?"}
              onSubmit={setTitle}
              variant="adorned"
            />
            <ProfileMenu profiles={Object.keys(ballots)} activeProfile={activeProfile} setProfileID={setProfileID} />
          </Stack>
          <SyncBar show={showSyncing} />
        </Paper>
        <EditableList
          ready={!!pollTitle}
          items={ballotFor(activeProfile, poll)}
          onAddItem={onAddCandidate}
          onRemoveItem={onRemoveItem}
          onReordered={(ranking: string[]) => {
            setShowSyncing(true)
            setTimeout(() => setShowSyncing(false), 750)
            onBallotUpdated(ranking)
          }}
        />
        <PollNavigation pollID={pollID} nav="ballot" />
      </PancakeStack>
    </>
  )
}

function pageTitle(pollTitle: string | null): string {
  return (pollTitle ? pollTitle + " | " : "") + "Druthers"
}

test("pageTitle", {
  "is 'Druthers' when the poll has no title yet"() {
    expect(pageTitle(null), is, "Druthers")
  },

  "is 'Druthers' when the poll's title is blank'"() {
    expect(pageTitle(""), is, "Druthers")
  },

  "includes the poll title when present'"() {
    expect(pageTitle("the title"), is, "the title | Druthers")
  },
})
